// src/shared/api/risk-assessment-service.ts
// EncounterMetaData Fall/Pain Assessment endpoints (Scrips.Patient, data-layer handoff
// 2026-09-13). Base path `/api/Patients` — routed through the same gateway host every other
// `get`/`post` call in this repo uses (no separate base URL needed).
//
// There is NO PUT/PATCH/DELETE for either resource: removing an entry is a LOCAL edit that only
// becomes durable via the next full-array POST (see `encounter/risk/services/use-risk-assessments.ts`).
import { get, post, ApiError } from './api-client';
import {
  decodeFallEnvelope,
  decodePainEnvelope,
  encodeFallEnvelope,
  encodePainEnvelope,
} from '@/encounter/risk/lib/risk-wire';
import type { FallAssessmentEntry, PainAssessmentEntry } from '@/encounter/risk/lib/risk-assessment-types';

function fallUrl(encounterId: string): string {
  return `/api/Patients/EncounterMetaData/${encodeURIComponent(encounterId)}/FallAssessment`;
}
function painUrl(encounterId: string): string {
  return `/api/Patients/EncounterMetaData/${encodeURIComponent(encounterId)}/PainAssessment`;
}

// PROD-3142 (Scrips.Patient #1051): the GET carries `version` (a hash of the stored list). A save that sends it back as
// `expectedVersion` is written only while the stored list is still that version; otherwise the API answers 412
// `stale-version` and writes nothing. A versioned save is answered with the new `version`. An API without versions
// sends none, and then nothing changes: no `expectedVersion` is sent.
export interface RiskReadOptions {
  signal?: AbortSignal;
  /** Called with the list's version when the API sends one. */
  onVersion?: (version: string) => void;
}
export interface RiskWriteOptions {
  signal?: AbortSignal;
  /** The version this list was edited from; absent → an unversioned save (last writer wins). */
  expectedVersion?: string;
  /** Sent as the page is hidden or unloaded: survive the page going away (`ApiCallOptions.keepalive`). */
  keepalive?: boolean;
}

/** The `version` of a GET or POST body, when the API sends one. */
function versionOf(json: unknown): string | undefined {
  const v = json && typeof json === 'object' ? (json as { version?: unknown }).version : undefined;
  return typeof v === 'string' && v !== '' ? v : undefined;
}

/** GET: a 404 means "nothing saved yet" — a normal state, mapped to an empty list rather than
 *  an error (spec §5). */
export async function getFallAssessments(
  encounterId: string,
  opts: RiskReadOptions = {},
): Promise<FallAssessmentEntry[]> {
  try {
    const json = await get<unknown>(fallUrl(encounterId), { signal: opts.signal });
    const version = versionOf(json);
    if (version) opts.onVersion?.(version);
    return decodeFallEnvelope(json);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return [];
    throw e;
  }
}

export async function getPainAssessments(
  encounterId: string,
  opts: RiskReadOptions = {},
): Promise<PainAssessmentEntry[]> {
  try {
    const json = await get<unknown>(painUrl(encounterId), { signal: opts.signal });
    const version = versionOf(json);
    if (version) opts.onVersion?.(version);
    return decodePainEnvelope(json);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return [];
    throw e;
  }
}

/**
 * POST fall assessments — ALWAYS the entire current array, never a delta (spec §1).
 *
 * The body is the plain envelope object — `post()` JSON.stringifies it exactly once. Pain is
 * serialized the same way (see `postPainAssessments`).
 *
 * PARITY-DEFECT(risk-save-errors): Flutter awaits this call and discards the result entirely —
 * a failed fall save surfaces NOTHING to the user. Reproduced deliberately for port parity (see
 * `services/use-risk-assessments.ts`, which is what actually swallows the rejection — this
 * function itself still resolves/rejects normally so a caller COULD observe it). A 404 here is
 * treated as success (spec §5): nothing to update yet is not a failure.
 */
export async function postFallAssessments(
  encounterId: string,
  list: readonly FallAssessmentEntry[],
  opts: RiskWriteOptions = {},
): Promise<string | undefined> {
  const body = opts.expectedVersion ? { ...encodeFallEnvelope(list), expectedVersion: opts.expectedVersion } : encodeFallEnvelope(list);
  try {
    return versionOf(await post<unknown>(fallUrl(encounterId), body, { signal: opts.signal, ...(opts.keepalive ? { keepalive: true } : {}) }));
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return undefined;
    throw e;
  }
}

/**
 * POST pain assessments — ALWAYS the entire current array, never a delta (spec §1).
 *
 * Single-encoded, exactly like fall: pass the PLAIN envelope object and let `post()` stringify it
 * once. Flutter's `jsonEncode` here is NOT a second encoding — its Dio client sends a String `data`
 * as-is (encounter_data_source_impl.dart `savePainAssessment`). This used to pre-stringify the
 * envelope on a misreading of that, so `post()` double-encoded it and the backend's
 * `[FromBody] JObject` rejected it: Pain never saved (PROD-2354, confirmed with Andrew 2026-09-25).
 */
export async function postPainAssessments(
  encounterId: string,
  list: readonly PainAssessmentEntry[],
  opts: RiskWriteOptions = {},
): Promise<string | undefined> {
  const body = opts.expectedVersion ? { ...encodePainEnvelope(list), expectedVersion: opts.expectedVersion } : encodePainEnvelope(list);
  try {
    return versionOf(await post<unknown>(painUrl(encounterId), body, { signal: opts.signal, ...(opts.keepalive ? { keepalive: true } : {}) }));
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return undefined;
    throw e;
  }
}
